/**
 * Bulk upload, on Invite People (Members page): a CSV or Excel sheet with the
 * columns First name, Last name, Email and Role, read into rows the admin
 * checks before anything is sent. Each ready row becomes one org invitation,
 * the same POST org/{authId}/members/invite as inviting one person.
 *
 * A row is skipped when its email already belongs to someone in the
 * organization or to a pending invitation, and when it repeats an earlier row
 * of the file. An empty Role invites them as a learner.
 *
 * No React and no server here, so it can be tested on its own (bulkInvite.test.ts).
 */

export type BulkRole = 'mentee' | 'mentor' | 'admin';

/** ready: sent on Invite. member / invited / duplicate: skipped. problem: fix the file. */
export type BulkRowState = 'ready' | 'member' | 'invited' | 'duplicate' | 'problem';

export interface BulkRow {
  /** Its line in the file, the header being line 1. */
  line: number;
  firstName: string;
  lastName: string;
  email: string;
  role: BulkRole | null;
  state: BulkRowState;
  /** Why it is skipped or can't be sent. */
  note: string;
}

export interface RoleLabels {
  mentee: string;
  mentor: string;
  admin: string;
}

export const TEMPLATE_HEADERS = ['First name', 'Last name', 'Email', 'Role'];
/** People per upload. */
export const BULK_LIMIT = 500;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Column = 'firstName' | 'lastName' | 'email' | 'role';
const COLUMN_BY_HEADER: Record<string, Column> = {
  firstname: 'firstName',
  first: 'firstName',
  givenname: 'firstName',
  lastname: 'lastName',
  last: 'lastName',
  surname: 'lastName',
  familyname: 'lastName',
  email: 'email',
  emailaddress: 'email',
  role: 'role'
};
const headerKey = (cell: unknown) =>
  String(cell ?? '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');
const text = (cell: unknown) => String(cell ?? '').trim();

/**
 * The role a Role cell names: the role code, the org's own word for it
 * (singular or plural, any case) or a common synonym. Empty is a learner.
 * Undefined when it names none of them.
 */
export function roleFromText(cell: string, labels: RoleLabels): BulkRole | undefined {
  const said = cell.trim().toLowerCase();
  if (!said) return 'mentee';
  const words: Record<BulkRole, string[]> = {
    mentee: ['mentee', 'learner', labels.mentee],
    mentor: ['mentor', labels.mentor],
    admin: ['admin', 'administrator', 'org admin', labels.admin]
  };
  const roles: BulkRole[] = ['mentee', 'mentor', 'admin'];
  return roles.find((role) =>
    words[role].some((w) => {
      const word = String(w || '')
        .trim()
        .toLowerCase();
      return !!word && (said === word || said === `${word}s` || said === `${word}es`);
    })
  );
}

/** "Learner, Manager or Admin": the roles a Role cell may name, in the org's words. */
export const roleChoicesText = (labels: RoleLabels) => `${labels.mentee}, ${labels.mentor} or ${labels.admin}`;

export interface ReadResult {
  rows: BulkRow[];
  /** The file as a whole can't be used (no header, too many people); rows is empty then. */
  fileProblem?: string;
}

/**
 * Reads a sheet's cells (first row the header) into rows. Header names are
 * matched loosely ("First name", "first_name", "FirstName"), and the columns
 * may come in any order.
 */
export function readRows(
  cells: unknown[][],
  ctx: { labels: RoleLabels; memberEmails: ReadonlySet<string>; invitedEmails: ReadonlySet<string> }
): ReadResult {
  const lines = cells.map((row, i) => ({ line: i + 1, row: Array.isArray(row) ? row : [] }));
  const filled = lines.filter(({ row }) => row.some((c) => text(c)));
  if (!filled.length) return { rows: [], fileProblem: 'The file is empty.' };

  const [header, ...body] = filled;
  const at: Partial<Record<Column, number>> = {};
  header.row.forEach((cell, i) => {
    const column = COLUMN_BY_HEADER[headerKey(cell)];
    if (column && at[column] === undefined) at[column] = i;
  });
  if (at.firstName === undefined || at.lastName === undefined || at.email === undefined) {
    return {
      rows: [],
      fileProblem: 'The first row needs the column names First name, Last name and Email (Role is optional).'
    };
  }
  if (!body.length) return { rows: [], fileProblem: 'The file has the column names but nobody under them.' };
  if (body.length > BULK_LIMIT) {
    return { rows: [], fileProblem: `The file has ${body.length} people. Upload up to ${BULK_LIMIT} at a time.` };
  }

  const cell = (row: unknown[], column: Column) => (at[column] === undefined ? '' : text(row[at[column] as number]));
  const firstLineOf = new Map<string, number>();
  const rows = body.map(({ line, row }): BulkRow => {
    const firstName = cell(row, 'firstName');
    const lastName = cell(row, 'lastName');
    const email = cell(row, 'email').toLowerCase();
    const roleCell = cell(row, 'role');
    const role = roleFromText(roleCell, ctx.labels);
    const base = { line, firstName, lastName, email, role: role ?? null };

    const missing = [!firstName && 'first name', !lastName && 'last name', !email && 'email'].filter(Boolean);
    if (missing.length) return { ...base, state: 'problem', note: `Missing ${missing.join(', ')}` };
    if (!EMAIL_RE.test(email)) return { ...base, state: 'problem', note: 'Not an email address' };
    if (!role) {
      return { ...base, state: 'problem', note: `Role "${roleCell}" isn't ${roleChoicesText(ctx.labels)}` };
    }
    const earlier = firstLineOf.get(email);
    if (earlier !== undefined) return { ...base, state: 'duplicate', note: `Same email as line ${earlier}` };
    firstLineOf.set(email, line);
    if (ctx.memberEmails.has(email)) return { ...base, state: 'member', note: 'Already in the organization' };
    if (ctx.invitedEmails.has(email)) return { ...base, state: 'invited', note: 'Already invited' };
    return { ...base, state: 'ready', note: '' };
  });
  return { rows };
}

export interface BulkCounts {
  ready: number;
  skipped: number;
  problems: number;
}

export const countRows = (rows: BulkRow[]): BulkCounts => ({
  ready: rows.filter((r) => r.state === 'ready').length,
  skipped: rows.filter((r) => r.state === 'member' || r.state === 'invited' || r.state === 'duplicate').length,
  problems: rows.filter((r) => r.state === 'problem').length
});

/** The template to fill in: the column names only, so nothing in it is ever invited by mistake. */
export const templateCsv = () => `${TEMPLATE_HEADERS.join(',')}\r\n`;

/**
 * Sends each ready row in turn, `concurrency` at a time, and says how each
 * went: true when sent, or the server's reason. One failure doesn't stop the others.
 */
export async function inviteEach(
  rows: BulkRow[],
  inviteOne: (row: BulkRow) => Promise<unknown>,
  onProgress?: (done: number) => void,
  concurrency = 3
): Promise<Map<number, true | string>> {
  const outcome = new Map<number, true | string>();
  let next = 0;
  const worker = async () => {
    while (next < rows.length) {
      const row = rows[next++];
      try {
        await inviteOne(row);
        outcome.set(row.line, true);
      } catch (e: any) {
        outcome.set(row.line, e?.response?.data?.message || e?.message || 'The invitation could not be sent');
      }
      onProgress?.(outcome.size);
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(concurrency, rows.length)) }, worker));
  return outcome;
}
